import { differenceInCalendarDays } from 'date-fns';
import { isNumeric, parseDateOrNull } from '@ianesthesie-kit/core';
import { ID } from '@ianesthesie-kit/services';
import {
  BillingAgingBucket,
  BillingLineDto,
  BillingPaymentDto,
  MinimalBillingLineDto
} from './billingLine.model';

/**
 * Pure, React-free derivations over a billing line, shared by the PDV receivables block,
 * the chase dashboard and the reminder flows. They all take a {@link MinimalBillingLineDto},
 * so a rule is stated once and cannot drift between the per-patient and dashboard views.
 * Anything needing hooks or i18n belongs in `billingLine.hooks.ts` / `billingLine.labels.ts`.
 */

/**
 * The consultation fee line of a surgical event. A patient's lines span all their events, so the
 * event is what tells this fee apart from a previous one's.
 */
export const findConsultationLine = (
  lines: BillingLineDto[] | undefined,
  surgicalEventId: ID | undefined
): BillingLineDto | undefined =>
  lines?.find(
    (l) =>
      l.lineType === 'CONSULTATION' && l.surgicalEventId === surgicalEventId
  );

/** The line's remaining balance; falls back to total − paid when the BFF omits it. */
export const lineBalance = (line: MinimalBillingLineDto): number =>
  line.balance ?? Math.max(0, (line.totalAmount ?? 0) - (line.amountPaid ?? 0));

/**
 * The line's effective aging clock, falling back to {@link BillingLineDto.agingAnchorDate} —
 * the care date — for older responses and whenever the two coincide (every line that has never
 * been reopened). A refund or lost chargeback is the one case they diverge: the care date stays
 * put while the clock restarts.
 */
const effectiveAgingClock = (line: MinimalBillingLineDto): string | undefined =>
  line.agingClockDate ?? line.agingAnchorDate;

/**
 * Derives the aging bucket of an OPEN line from its aging clock (current < 30d, overdue 30–60d
 * inclusive, critical > 60d) — the same thresholds as the backend's `AgingBuckets.of`, but not
 * the same function: that overload also takes `underLiveStripeHold` and pins a held line to
 * CURRENT, and its javadoc requires every derivation to state what it knows about the line's
 * card hold. This one knows nothing — the per-patient line DTO carries no hold state — so a line
 * held by a live two-bite hold can read CURRENT on the (server-computed) dashboard bucket while
 * this derives OVERDUE/CRITICAL for the same line on the PDV. Threading the hold flag through
 * here is known follow-up work, not yet done.
 */
export const deriveAgingBucket = (
  line: MinimalBillingLineDto
): BillingAgingBucket | undefined => {
  if (line.storedStatus !== 'OPEN') {
    return undefined;
  }
  const clock = parseDateOrNull(effectiveAgingClock(line));
  // An unanchored open line is CURRENT, not bucketless — same branch as `AgingBuckets.of`
  // in medical-synthesis. An acte whose devis is only printed has not started its payment
  // clock (and is skipped by the reminder engine), but it is still an open receivable, so
  // it must not read as stateless here while the dashboard shows it as "Actuel".
  if (!clock) {
    return 'CURRENT';
  }
  const days = differenceInCalendarDays(new Date(), clock);
  if (days < 30) {
    return 'CURRENT';
  }
  return days <= 60 ? 'OVERDUE' : 'CRITICAL';
};

/**
 * Full days elapsed since the line's aging clock, for surfaces that spell the age out
 * next to the bucket ("Critique · 75 j") instead of only naming it. `undefined` when the
 * line has no clock yet (an acte whose devis is not sent) — there is no clock to report —
 * and negative for a future date, which the callers drop rather than render as "-3 j".
 * Calendar days, like {@link deriveAgingBucket}, so the number and the bucket it qualifies
 * can never disagree at a boundary.
 */
export const daysSinceAnchor = (
  line: MinimalBillingLineDto
): number | undefined => {
  const clock = parseDateOrNull(effectiveAgingClock(line));
  return clock ? differenceInCalendarDays(new Date(), clock) : undefined;
};

/**
 * A line is eligible for a chase reminder unless it is already settled, exonerated, or
 * has nothing left to collect — the three hard stops `BillingReminderService`
 * (medical-synthesis) applies to `/billing/line/remind` and `/billing/line/{id}/remind`
 * before dispatching: `SKIPPED_PAID` on a PAID line, `SKIPPED_EXEMPT` on an exempt one,
 * and `SKIPPED_PAID` again on a cleared balance (`balance.signum() <= 0`), since a line
 * zeroed by an amount override must never be dunned for "0,00 €".
 * Shared by `BulkRemindDialog` (recipient count + the posted line ids) and both surfaces'
 * "Remind now" affordances (dashboard row menu, PDV bell), so a line that cannot be
 * reminded is never counted, posted, nor offered the action. Row selection itself is NOT
 * gated on this — settled lines stay selectable for the selection-scoped export.
 */
export const isRemindEligible = (line: MinimalBillingLineDto): boolean =>
  line.storedStatus !== 'PAID' &&
  line.storedStatus !== 'EXEMPT' &&
  lineBalance(line) > 0;

/**
 * Euro decimal with centime precision, e.g. `31.5` / `31.50` (never more than 2 decimals).
 * The kit `NumericInput` emits plain strings with a `.` separator, so every euro amount is
 * validated as a string before being parsed. Both the BFF (`@PositiveOrZero @Digits(integer = 8,
 * fraction = 2)`) and medical-synthesis (business code `E1-12-2-1052`) refuse a negative amount
 * or more than two decimals — client-side validation keeps such a value from ever being sent.
 */
export const EURO_AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;

/** Largest amount the backend accepts (`@Digits(integer = 8, fraction = 2)`). */
export const MAX_EURO_AMOUNT = 99_999_999.99;

/**
 * Parses a typed euro amount. Returns `undefined` unless the text is a non-negative,
 * cent-precise amount within {@link MAX_EURO_AMOUNT}, i.e. one the backend will accept.
 */
export const parseEuroAmount = (text: string): number | undefined =>
  isNumeric(text) && EURO_AMOUNT_REGEX.test(text) && +text <= MAX_EURO_AMOUNT
    ? +text
    : undefined;

/** Compares amounts in centimes so binary float noise never rejects an exact balance. */
export const toCentimes = (amount: number) => Math.round(amount * 100);

/** Lines still owing money come first; the clock half of the order is {@link agingRank}. */
const collectionRank = (line: MinimalBillingLineDto) =>
  line.storedStatus === 'OPEN' && lineBalance(line) > 0 ? 0 : 1;

/**
 * Aging sort key: a clockless line (an acte whose devis is not sent yet) sorts last. Reads
 * {@link effectiveAgingClock}, not the raw `agingAnchorDate` — a line a refund or lost chargeback
 * reopened must sort by when its clock restarted, not by its original care date, or it outranks
 * genuinely older debt in this very "most overdue first" list. `MAX_SAFE_INTEGER` rather than
 * `Infinity` so two clockless lines subtract to 0 instead of `NaN`, which would make the
 * comparator inconsistent.
 */
const agingRank = (line: MinimalBillingLineDto) =>
  parseDateOrNull(effectiveAgingClock(line))?.getTime() ??
  Number.MAX_SAFE_INTEGER;

/**
 * Chasing order for a patient's lines: those still owing money lead, most overdue first,
 * clockless ones after them, settled (PAID / EXEMPT) lines last. Needed because the BFF
 * orders lines by aging anchor ascending with nulls first, which lets a settled or
 * not-yet-anchored line outrank an overdue open one. Shared by the PDV receivables block
 * and the chase dashboard's patient drawer so both list the same patient identically.
 */
export const compareLinesForCollection = (
  a: MinimalBillingLineDto,
  b: MinimalBillingLineDto
): number =>
  collectionRank(a) - collectionRank(b) || agingRank(a) - agingRank(b);

/**
 * A refund gives a payment back instead of adding one, and medical-synthesis books it as a
 * payment row with a negative amount. The sign is the whole rule: a refund keeps the method of
 * the capture it undoes (CARD / ONLINE), so nothing else on the row tells the two apart.
 */
export const isRefundPayment = (payment: BillingPaymentDto): boolean =>
  payment.amount < 0;

/**
 * A refund Stripe failed or canceled after it was booked is walked back by a positive row that
 * puts the amount back on the line (`recordStripeRefundFailed`), pointing at the refund it
 * compensates. It is therefore the one positive row carrying `refundOfPaymentId` — the sign alone
 * does not tell it from money the desk actually collected.
 */
export const isRefundCompensationPayment = (
  payment: BillingPaymentDto
): boolean => payment.amount > 0 && payment.refundOfPaymentId != null;

/**
 * Whether a receipt can still be issued for a payment, mirroring the two guards
 * `BillingReceiptService.renderReceipt` applies before rendering one (400 otherwise): the row must
 * add money, and it must not reverse another row — which rules out the refund and the compensation
 * that walks a failed one back alike, neither of which is ever issued a receipt. On top of that, a
 * payment a refund gave back has had its receipt withdrawn (`receiptVoidedAt`): that download is
 * refused too and the patient's own copy is deleted, so a surface still offering any of the three
 * would only produce an error.
 */
export const hasDownloadableReceipt = (payment: BillingPaymentDto): boolean =>
  payment.amount > 0 &&
  payment.refundOfPaymentId == null &&
  !payment.receiptVoidedAt;
